import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, Layers, Copy, Eye, EyeOff, Star, ArrowUp, ArrowDown,
  Car, Home, Heart, Stethoscope, Activity, Plane, Shield, Bike, Anchor, Briefcase, Building2, Truck, Tractor, Wrench, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

// Mappa nome icona → componente lucide-react
const ICONS = {
  car: Car, bike: Bike, home: Home, heart: Heart, stethoscope: Stethoscope,
  activity: Activity, plane: Plane, shield: Shield, anchor: Anchor,
  briefcase: Briefcase, building2: Building2, truck: Truck, tractor: Tractor,
  wrench: Wrench, sparkles: Sparkles,
};
const ICON_OPTIONS = Object.keys(ICONS);
const COLOR_OPTIONS = ["blue", "emerald", "rose", "teal", "orange", "sky", "indigo", "violet", "amber", "slate", "red", "fuchsia", "lime"];

// Rami fiscali standard (configurabili in Impostazioni → Aliquote).
// I tipi polizza personalizzati (moto, assistenza_stradale, ecc.) devono
// puntare a un ramo fiscale base per consentire il calcolo automatico
// di imposte e SSN nelle polizze.
const TAX_RAMI = [
  { v: "auto", l: "Auto (RC veicoli)" },
  { v: "casa", l: "Casa / Multirischio" },
  { v: "vita", l: "Vita" },
  { v: "salute", l: "Salute" },
  { v: "infortuni", l: "Infortuni" },
  { v: "viaggio", l: "Viaggio" },
  { v: "responsabilita_civile", l: "Responsabilità civile" },
];

const COLOR_CLASSES = {
  blue: "bg-blue-50 border-blue-200 text-blue-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  teal: "bg-teal-50 border-teal-200 text-teal-700",
  orange: "bg-orange-50 border-orange-200 text-orange-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  red: "bg-red-50 border-red-200 text-red-700",
  fuchsia: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",
  lime: "bg-lime-50 border-lime-200 text-lime-700",
};

const emptyForm = { code: "", label: "", icon: "shield", color: "slate", sort_order: 100, tax_ramo: "", vehicle_fields_enabled: false, vehicle_fields_selected: [], required_fields: [] };

// 🆕 Campi veicolo selezionabili per ogni tipologia
const VEHICLE_FIELDS = [
  { v: "vehicle_plate", l: "Targa" },
  { v: "vehicle_brand", l: "Marca" },
  { v: "vehicle_model", l: "Modello" },
  { v: "vehicle_first_registration", l: "Data prima immatricolazione" },
  { v: "vehicle_fuel", l: "Alimentazione" },
  { v: "vehicle_kw", l: "Potenza (kW)" },
  { v: "vehicle_displacement", l: "Cilindrata (cc)" },
  { v: "chassis_number", l: "Numero telaio" },
  { v: "vehicle_purchase_date", l: "Data acquisto veicolo" },
  { v: "vehicle_seats", l: "Numero posti" },
  { v: "vehicle_color", l: "Colore" },
];

// 🆕 Campi proprietario sempre disponibili come "obbligatori per tipologia"
const OWNER_FIELDS = [
  { v: "owner_name", l: "Nome proprietario" },
  { v: "owner_surname", l: "Cognome proprietario" },
  { v: "owner_fiscal_code", l: "Codice fiscale proprietario" },
  { v: "owner_birth_date", l: "Data di nascita proprietario" },
  { v: "owner_email", l: "Email proprietario" },
  { v: "owner_phone", l: "Telefono / cellulare proprietario" },
  { v: "owner_address", l: "Indirizzo proprietario" },
];

export default function PolicyTypes() {
  const { canWrite } = usePermissions();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/policy-types?include_inactive=true"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 🆕 Toggle attiva/disattiva tipologia — funziona anche sui system per agenzia
  const toggleActive = async (t) => {
    const nextActive = t.is_active === false ? true : false;
    try {
      await api.patch(`/policy-types/${t.id}/active`, { is_active: nextActive });
      toast.success(nextActive ? `"${t.label}" attivata` : `"${t.label}" disattivata`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Toggle pin (favorite): appare in cima nel wizard
  const togglePin = async (t) => {
    const nextPinned = !t.is_pinned;
    try {
      await api.patch(`/policy-types/${t.id}/pin`, { is_pinned: nextPinned });
      toast.success(nextPinned ? `⭐ "${t.label}" aggiunto ai preferiti` : `Rimosso "${t.label}" dai preferiti`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Riordino inline con frecce ↑↓ (scambia sort_order con adiacente)
  const [moving, setMoving] = useState(null);
  const move = async (t, dir) => {
    setMoving(t.id);
    try {
      const { data } = await api.patch(`/policy-types/${t.id}/move`, { direction: dir });
      if (data?.ok === false) {
        toast.info(data.reason || "Già all'estremo");
      } else {
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMoving(null); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...emptyForm, ...t, tax_ramo: t.tax_ramo || "", vehicle_fields_enabled: !!t.vehicle_fields_enabled, vehicle_fields_selected: Array.isArray(t.vehicle_fields_selected) ? t.vehicle_fields_selected : [], required_fields: Array.isArray(t.required_fields) ? t.required_fields : [] }); setOpen(true); };
  // 🆕 Duplica un record di sistema come custom per l'agenzia corrente.
  const openDuplicate = (t) => {
    setEditing(null); // → forzerà una POST (creazione)
    setForm({
      ...emptyForm,
      code: `${t.code || ""}_copy`,
      label: `${t.label || ""} (Copia)`,
      icon: t.icon || "shield",
      color: t.color || "slate",
      sort_order: (t.sort_order || 100) + 1,
      tax_ramo: t.tax_ramo || "",
      vehicle_fields_enabled: !!t.vehicle_fields_enabled,
      vehicle_fields_selected: Array.isArray(t.vehicle_fields_selected) ? [...t.vehicle_fields_selected] : [],
      required_fields: Array.isArray(t.required_fields) ? [...t.required_fields] : [],
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order) || 100,
        tax_ramo: form.tax_ramo || null,
        vehicle_fields_enabled: !!form.vehicle_fields_enabled,
        vehicle_fields_selected: form.vehicle_fields_enabled ? (form.vehicle_fields_selected || []) : [],
        required_fields: Array.isArray(form.required_fields) ? form.required_fields : [],
      };
      if (editing) await api.put(`/policy-types/${editing.id}`, payload);
      else await api.post("/policy-types", payload);
      toast.success(editing ? "Tipologia aggiornata" : "Tipologia creata");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = (t) => setDeleteTarget(t);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/policy-types/${deleteTarget.id}`);
      toast.success("Tipologia eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const PreviewIcon = ICONS[form.icon] || Shield;
  const isFormEmoji = !ICONS[form.icon] && !!form.icon;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="policy-types-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Configurazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Tipologie Polizze</h1>
          <p className="text-slate-500 mt-1">{items.length} tipologie · personalizza l&apos;elenco visibile in tutto il CRM</p>
        </div>
        {canWrite("categories") && (
          <Button data-testid="add-type-btn" onClick={openCreate} className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Plus className="h-4 w-4 mr-2" /> Nuova tipologia
          </Button>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-violet-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Anteprima</th>
              <th className="text-left px-4 py-3">Codice</th>
              <th className="text-left px-4 py-3">Etichetta</th>
              <th className="text-left px-4 py-3">Icona</th>
              <th className="text-left px-4 py-3">Colore</th>
              <th className="text-right px-4 py-3">Ord.</th>
              <th className="text-right px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="policy-types-table">
            {items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Nessuna tipologia.</td></tr>}
            {items.map((t) => {
              const isEmoji = !ICONS[t.icon] && !!t.icon;
              const Icon = ICONS[t.icon] || Shield;
              const colorCls = COLOR_CLASSES[t.color] || COLOR_CLASSES.slate;
              return (
                <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${t.is_active === false ? "opacity-50" : ""} ${t.is_pinned ? "bg-amber-50/30" : ""}`} data-testid={`type-row-${t.code}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full border ${colorCls}`}>
                      {isEmoji ? (
                        <span className="text-sm leading-none" data-testid={`type-emoji-${t.code}`}>{t.icon}</span>
                      ) : (
                        <Icon className="h-3.5 w-3.5" />
                      )}
                      {t.label}
                    </span>
                    {t.is_pinned && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700" data-testid={`type-pinned-badge-${t.code}`}>
                        <Star className="h-2.5 w-2.5 fill-amber-500" /> Preferita
                      </span>
                    )}
                    {t.is_active === false && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-red-300 bg-red-50 text-red-700" data-testid={`type-inactive-badge-${t.code}`}>
                        Disattivata
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">
                    {t.code}
                    {(t.is_system || !t.agency_id) ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600" title="Tipologia di sistema condivisa (non modificabile)">🔒 Sistema</span>
                    ) : (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700" title="Custom della tua agenzia">✏️ Custom</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.label}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{t.icon}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{t.color}</td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">{t.sort_order}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {canWrite("categories") && (
                      <>
                        {/* 🆕 Frecce riordino inline */}
                        <button
                          data-testid={`move-up-${t.code}`}
                          onClick={() => move(t, "up")}
                          disabled={moving === t.id}
                          className="text-slate-500 hover:text-slate-800 disabled:opacity-30"
                          title="Sposta su"
                        >
                          <ArrowUp className="h-3.5 w-3.5 inline" />
                        </button>
                        <button
                          data-testid={`move-down-${t.code}`}
                          onClick={() => move(t, "down")}
                          disabled={moving === t.id}
                          className="text-slate-500 hover:text-slate-800 disabled:opacity-30"
                          title="Sposta giù"
                        >
                          <ArrowDown className="h-3.5 w-3.5 inline" />
                        </button>
                        {/* 🆕 Pin (preferite) */}
                        <button
                          data-testid={`toggle-pin-${t.code}`}
                          onClick={() => togglePin(t)}
                          className={t.is_pinned ? "text-amber-500 hover:text-amber-600" : "text-slate-300 hover:text-amber-500"}
                          title={t.is_pinned ? "Rimuovi dai preferiti (⭐)" : "Aggiungi ai preferiti (⭐ in cima al wizard)"}
                        >
                          <Star className={`h-3.5 w-3.5 inline ${t.is_pinned ? "fill-amber-400" : ""}`} />
                        </button>
                        {/* 🆕 Toggle attiva/disattiva */}
                        <button
                          data-testid={`toggle-active-${t.code}`}
                          onClick={() => toggleActive(t)}
                          className={t.is_active === false ? "text-slate-400 hover:text-emerald-700" : "text-emerald-600 hover:text-emerald-800"}
                          title={t.is_active === false ? "Attiva tipologia" : "Disattiva (nascondila dal wizard senza cancellarla)"}
                        >
                          {t.is_active === false ? <EyeOff className="h-3.5 w-3.5 inline" /> : <Eye className="h-3.5 w-3.5 inline" />}
                        </button>
                        {(t.is_system || !t.agency_id) ? (
                          <>
                            <span className="text-slate-300 cursor-not-allowed" title="Tipologia di sistema — non modificabile. Creane una custom.">
                              <Pencil className="h-3.5 w-3.5 inline" />
                            </span>
                            <button
                              data-testid={`duplicate-type-${t.code}`}
                              onClick={() => openDuplicate(t)}
                              className="text-emerald-600 hover:text-emerald-800"
                              title="Duplica come custom per la tua agenzia"
                            >
                              <Copy className="h-3.5 w-3.5 inline" />
                            </button>
                          </>
                        ) : (
                          <button data-testid={`edit-type-${t.code}`} onClick={() => openEdit(t)} className="text-blue-600 hover:text-blue-800" title="Modifica">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {!(t.is_system || !t.agency_id) && (
                          <button data-testid={`delete-type-${t.code}`} onClick={() => remove(t)} className="text-red-600 hover:text-red-800" title="Elimina">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialog create/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="type-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-blue-600" />
              {editing ? "Modifica tipologia" : "Nuova tipologia"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Codice tecnico *</Label>
              <Input
                data-testid="type-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={editing?.is_system}
                placeholder="es. moto, nautica, professionale"
                className="font-mono lowercase"
              />
              <p className="text-[10px] text-slate-500 mt-1">Solo lettere minuscole, numeri e _</p>
            </div>
            <div>
              <Label>Etichetta *</Label>
              <Input
                data-testid="type-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="es. Moto, Nautica, Professionale"
              />
            </div>
            <div>
              <Label>Icona</Label>
              <Select value={ICONS[form.icon] ? form.icon : "shield"} onValueChange={(v) => setForm({ ...form, icon: v })}>
                <SelectTrigger data-testid="type-icon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((ic) => {
                    const I = ICONS[ic];
                    return (
                      <SelectItem key={ic} value={ic}>
                        <span className="inline-flex items-center gap-2"><I className="h-3.5 w-3.5" /> {ic}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {/* 🆕 Feb 2026 — Emoji custom in alternativa alle icone lucide */}
              <div className="mt-2">
                <Label className="text-[11px] text-slate-500 uppercase tracking-wider">Oppure emoji personalizzata</Label>
                <Input
                  data-testid="type-icon-emoji"
                  value={!ICONS[form.icon] && form.icon ? form.icon : ""}
                  onChange={(e) => setForm({ ...form, icon: e.target.value.trim() || "shield" })}
                  placeholder="es. 🚁 🏂 🐎 (lascia vuoto per usare l'icona sopra)"
                  maxLength={8}
                />
              </div>
            </div>
            <div>
              <Label>Colore</Label>
              <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                <SelectTrigger data-testid="type-color"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map((co) => (
                    <SelectItem key={co} value={co}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-3 w-3 rounded-full inline-block border ${COLOR_CLASSES[co] || ""}`} />
                        {co}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Ordine di visualizzazione</Label>
              <Input
                type="number"
                data-testid="type-order"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                placeholder="100"
              />
              <p className="text-[10px] text-slate-500 mt-1">Numero più basso = appare prima nelle liste.</p>
            </div>
            <div className="col-span-2">
              <Label className="flex items-center gap-1.5">
                Ramo fiscale (per imposte/SSN)
                <span className="text-[10px] font-normal text-slate-400">facoltativo</span>
              </Label>
              <Select value={form.tax_ramo || "__none__"} onValueChange={(v) => setForm({ ...form, tax_ramo: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="type-tax-ramo"><SelectValue placeholder="Auto-rileva (consigliato)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Auto-rileva da nome/icona —</SelectItem>
                  {TAX_RAMI.map((r) => (
                    <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1">Indica quale ramo fiscale usare per il calcolo automatico di imposte e SSN. Le tipologie come <em>Moto</em>, <em>Assistenza stradale</em>, <em>Tutela legale</em> in genere usano <strong>Auto</strong>.</p>
            </div>
            {/* 🆕 Sezione dati veicolo collegati */}
            <div className="col-span-2 border-2 border-amber-200 bg-amber-50/40 rounded-md p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <Label className="flex items-center gap-2 m-0">
                  🚗 Collega dati veicolo a questa tipologia
                </Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="type-vehicle-enabled"
                    checked={!!form.vehicle_fields_enabled}
                    onChange={(e) => setForm({ ...form, vehicle_fields_enabled: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <span className="text-xs font-semibold text-amber-800">{form.vehicle_fields_enabled ? "ATTIVO" : "Disattivo"}</span>
                </label>
              </div>
              {form.vehicle_fields_enabled ? (
                <div>
                  <p className="text-[11px] text-slate-600 mb-2">Seleziona quali campi veicolo verranno mostrati nei preventivi/polizze di questa tipologia:</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {VEHICLE_FIELDS.map((vf) => (
                      <label key={vf.v} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-amber-100 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`type-vf-${vf.v}`}
                          checked={(form.vehicle_fields_selected || []).includes(vf.v)}
                          onChange={(e) => {
                            const cur = new Set(form.vehicle_fields_selected || []);
                            if (e.target.checked) cur.add(vf.v); else cur.delete(vf.v);
                            setForm({ ...form, vehicle_fields_selected: [...cur] });
                          }}
                          className="h-3.5 w-3.5"
                        />
                        <span>{vf.l}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2 pt-2 border-t border-amber-200">
                    <button
                      type="button"
                      className="text-[11px] text-amber-700 hover:underline"
                      onClick={() => setForm({ ...form, vehicle_fields_selected: VEHICLE_FIELDS.map((v) => v.v) })}
                    >Seleziona tutti</button>
                    <span className="text-amber-400 text-[11px]">·</span>
                    <button
                      type="button"
                      className="text-[11px] text-amber-700 hover:underline"
                      onClick={() => setForm({ ...form, vehicle_fields_selected: [] })}
                    >Deseleziona tutti</button>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 italic">I dati veicolo NON saranno richiesti né mostrati per questa tipologia.</p>
              )}
            </div>
            {/* 🆕 Campi obbligatori al salvataggio del preventivo per questa tipologia */}
            <div className="col-span-2 border-2 border-rose-200 bg-rose-50/40 rounded-md p-3">
              <Label className="flex items-center gap-2 m-0 mb-2">
                ✳️ Campi obbligatori al salvataggio del preventivo
              </Label>
              <p className="text-[11px] text-slate-600 mb-2">
                Seleziona quali campi devono essere obbligatoriamente compilati prima di poter salvare un preventivo di questa tipologia.
                Il preventivo non si potrà salvare se uno dei campi obbligatori è vuoto.
              </p>
              {form.vehicle_fields_enabled && (form.vehicle_fields_selected || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase font-semibold text-rose-700 mb-1">Dati veicolo</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(form.vehicle_fields_selected || []).map((vfCode) => {
                      const vf = VEHICLE_FIELDS.find((x) => x.v === vfCode);
                      if (!vf) return null;
                      return (
                        <label key={vf.v} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-rose-100 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`type-required-${vf.v}`}
                            checked={(form.required_fields || []).includes(vf.v)}
                            onChange={(e) => {
                              const cur = new Set(form.required_fields || []);
                              if (e.target.checked) cur.add(vf.v); else cur.delete(vf.v);
                              setForm({ ...form, required_fields: [...cur] });
                            }}
                            className="h-3.5 w-3.5"
                          />
                          <span>{vf.l}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase font-semibold text-rose-700 mb-1">Dati proprietario / cliente</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {OWNER_FIELDS.map((of) => (
                    <label key={of.v} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-rose-100 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`type-required-${of.v}`}
                        checked={(form.required_fields || []).includes(of.v)}
                        onChange={(e) => {
                          const cur = new Set(form.required_fields || []);
                          if (e.target.checked) cur.add(of.v); else cur.delete(of.v);
                          setForm({ ...form, required_fields: [...cur] });
                        }}
                        className="h-3.5 w-3.5"
                      />
                      <span>{of.l}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-2 pt-2 border-t border-rose-200">
                <button
                  type="button"
                  className="text-[11px] text-rose-700 hover:underline"
                  onClick={() => setForm({ ...form, required_fields: [] })}
                >Deseleziona tutti</button>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Anteprima</Label>
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full border ${COLOR_CLASSES[form.color] || COLOR_CLASSES.slate}`}>
                  {isFormEmoji ? (
                    <span className="text-sm leading-none">{form.icon}</span>
                  ) : (
                    <PreviewIcon className="h-3.5 w-3.5" />
                  )}
                  {form.label || "Etichetta"}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-type" disabled={!form.code.trim() || !form.label.trim()} onClick={submit} className="bg-[#0F172A] hover:bg-[#1E293B]">
              {editing ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-type-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare la tipologia?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per eliminare:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">{deleteTarget?.label}</p>
            <p className="text-xs text-red-600">Le polizze già collegate manterranno il codice ma la tipologia non sarà più disponibile per nuove polizze.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-type" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annulla</Button>
            <Button data-testid="confirm-delete-type" onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Eliminazione…" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
